import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Paragraph, Text } from 'components/core/Typography';
import MapPin from 'components/svg/MapPin';

import './NearMeSubheader.scss';

// Matches $geoLinkBlue in _variables.scss
const GEO_LINK_BLUE = '#2f97e6';

function getStatusMessage({
	geoError,
	hasCoords,
	city,
	state,
	locating,
	isUpdating,
}) {
	if (geoError) {
		return (
			<Text
				size="tiny"
				color="white"
				last
			>
				We could not determine your precise location. Please enable
				location in your browser settings.
			</Text>
		);
	}

	// Show "location has been set" when:
	// 1. We have coords and NOT currently locating (normal case after first fetch)
	// 2. We're updating an existing location (keep showing "set to" during reload)
	if (hasCoords && (!locating || isUpdating)) {
		return city && state
			? `Your location has now been set to ${city}, ${state}.`
			: 'Your location has been updated.';
	}

	return city
		? `It looks like you're in ${city}. Not correct?`
		: 'Find storage near you.';
}

export default function NearMeSubheader({
	className,
	geoError,
	searchLocation,
	locateMe,
	locating,
	hasCoords,
	isUpdating,
}) {
	const buttonLabel =
		geoError || !hasCoords ? 'Get current location' : 'Update Location';

	const city = searchLocation?.city;
	const state = searchLocation?.state;
	const statusMessage = getStatusMessage({
		geoError,
		hasCoords,
		city,
		state,
		locating,
		isUpdating,
	});

	return (
		<Paragraph
			className={classnames('near-me-subheader', className)}
			color="white"
			size="tiny"
			weight="medium"
			last
		>
			{statusMessage}{' '}
			<button
				type="button"
				onClick={locateMe}
				disabled={locating}
				aria-label="Use my current location to find storage near me"
				className="near-me-subheader__btn"
			>
				<span className="near-me-subheader__icon">
					<MapPin fill={GEO_LINK_BLUE} />
				</span>{' '}
				<span>{buttonLabel}</span>
			</button>
		</Paragraph>
	);
}

NearMeSubheader.propTypes = {
	className: PropTypes.string,
	geoError: PropTypes.bool,
	searchLocation: PropTypes.shape({
		city: PropTypes.string,
		state: PropTypes.string,
	}),
	locateMe: PropTypes.func.isRequired,
	locating: PropTypes.bool,
	hasCoords: PropTypes.bool,
	isUpdating: PropTypes.bool,
};

NearMeSubheader.defaultProps = {
	geoError: false,
	locating: false,
	hasCoords: false,
	isUpdating: false,
};
